/**
 * Admin de categorías de proyecto (web, ecommerce, cloud, mobile, ...).
 * Configurable: nombre, slug, color, orden, activo.
 *
 * Las categorías se asignan many-to-many a proyectos desde el form
 * de edición del proyecto (vía CategoriesPicker).
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ArrowLeft, Pencil, Plus, Tags, Trash2 } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { api } from "@/lib/api";

export const Route = createFileRoute("/admin/project-categories")({
  component: ProjectCategoriesPage,
});

interface Category {
  id: string;
  name: string;
  slug: string;
  color: string | null;
  displayOrder: number;
  active: boolean;
  createdAt: number;
}

function ProjectCategoriesPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<{ categories: Category[] }>({
    queryKey: ["project-categories"],
    queryFn: () => api.get("/project-categories"),
  });
  const [editing, setEditing] = useState<Category | "new" | null>(null);
  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/project-categories/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["project-categories"] }),
  });

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="admin"
        title="Categorías de proyecto"
        subtitle="Catálogo configurable de tipos de proyecto. Se asignan many-to-many desde el form de edición del proyecto."
        actions={
          <>
            <Link to="/admin" className="btn btn-ghost btn-sm">
              <ArrowLeft className="size-4" /> Admin
            </Link>
            <button
              type="button"
              onClick={() => setEditing("new")}
              className="btn btn-primary btn-sm"
            >
              <Plus className="size-4" /> Nueva categoría
            </button>
          </>
        }
      />

      {isLoading ? (
        <div className="grid place-items-center py-16">
          <span className="loading loading-spinner loading-lg text-primary" />
        </div>
      ) : !data?.categories.length ? (
        <div className="card-elevated p-10 text-center text-sm text-base-content/55">
          Sin categorías. Crea la primera con el botón arriba.
        </div>
      ) : (
        <section className="card-elevated overflow-hidden">
          <ul className="divide-y divide-base-300/40">
            {data.categories.map((c) => (
              <li key={c.id} className="p-4 flex items-center gap-3 flex-wrap">
                <div className="size-9 rounded-lg grid place-items-center" style={c.color ? { backgroundColor: `${c.color}20`, color: c.color } : {}}>
                  <Tags className="size-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium flex items-center gap-2">
                    {c.name}
                    {!c.active && <span className="badge badge-ghost badge-xs">Inactiva</span>}
                  </div>
                  <div className="text-xs text-base-content/55 font-mono">{c.slug}</div>
                </div>
                <span className="text-xs text-base-content/45 tabular-nums">
                  orden {c.displayOrder}
                </span>
                <button
                  type="button"
                  onClick={() => setEditing(c)}
                  className="btn btn-ghost btn-xs"
                  title="Editar"
                >
                  <Pencil className="size-3" />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (confirm(`¿Borrar "${c.name}"? Las asignaciones a proyectos se eliminan en cascada.`))
                      remove.mutate(c.id);
                  }}
                  disabled={remove.isPending}
                  className="btn btn-ghost btn-xs text-error"
                  title="Borrar"
                >
                  <Trash2 className="size-3" />
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {editing !== null && (
        <CategoryDialog
          category={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ["project-categories"] });
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function CategoryDialog({
  category,
  onClose,
  onSaved,
}: {
  category: Category | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(category?.name ?? "");
  const [slug, setSlug] = useState(category?.slug ?? "");
  const [color, setColor] = useState(category?.color ?? "#22d3ee");
  const [displayOrder, setDisplayOrder] = useState(category?.displayOrder ?? 50);
  const [active, setActive] = useState(category?.active ?? true);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const body = { name, slug, color: color || null, displayOrder, active };
      return category
        ? api.patch(`/project-categories/${category.id}`, body)
        : api.post("/project-categories", body);
    },
    onSuccess: onSaved,
    onError: (e) => setError((e as Error).message),
  });

  return (
    <div
      className="modal modal-open"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal-box max-w-md">
        <h3 className="font-display font-semibold text-lg mb-3">
          {category ? "Editar categoría" : "Nueva categoría"}
        </h3>
        <div className="space-y-3">
          <label className="form-control w-full">
            <span className="label-text text-xs font-semibold uppercase tracking-wider text-base-content/60 mb-1">
              Nombre
            </span>
            <input
              className="input input-bordered w-full"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (!category) setSlug(slugify(e.target.value));
              }}
              autoFocus
            />
          </label>
          <label className="form-control w-full">
            <span className="label-text text-xs font-semibold uppercase tracking-wider text-base-content/60 mb-1">
              Slug (a-z, 0-9, guiones)
            </span>
            <input
              className="input input-bordered w-full font-mono"
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
            />
          </label>
          <div className="grid grid-cols-2 gap-2">
            <label className="form-control">
              <span className="label-text text-xs font-semibold uppercase tracking-wider text-base-content/60 mb-1">
                Color
              </span>
              <input
                type="color"
                className="input input-bordered w-full h-10"
                value={color}
                onChange={(e) => setColor(e.target.value)}
              />
            </label>
            <label className="form-control">
              <span className="label-text text-xs font-semibold uppercase tracking-wider text-base-content/60 mb-1">
                Orden
              </span>
              <input
                type="number"
                className="input input-bordered w-full"
                min={0}
                max={9999}
                value={displayOrder}
                onChange={(e) => setDisplayOrder(parseInt(e.target.value, 10) || 0)}
              />
            </label>
          </div>
          <label className="cursor-pointer flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="toggle toggle-sm toggle-primary"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
            />
            Activa
          </label>
        </div>
        {error && (
          <div className="mt-3 text-xs text-error border border-error/30 bg-error/5 rounded p-2">
            {error}
          </div>
        )}
        <div className="modal-action">
          <button type="button" onClick={onClose} className="btn btn-ghost btn-sm">
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => save.mutate()}
            disabled={save.isPending || !name.trim() || !slug.trim()}
            className="btn btn-primary btn-sm"
          >
            {save.isPending && <span className="loading loading-spinner loading-xs" />}
            Guardar
          </button>
        </div>
      </div>
    </div>
  );
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
}
